import { useEffect, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import baseURL from "../../configs/api";
import { CircularProgress, Menu, MenuItem } from "@mui/material";
import { useTheme } from "../../components/hooks/useTheme";
import Cookies from "js-cookie";
import useTenant from "../../components/hooks/useTenant";
import moment from "moment-timezone";
import useGeneral from "../../components/hooks/useGeneral";
import colorConfigs from "../../configs/colorConfigs";

const days = ["7 Days", "14 Days", "30 Days"];

type Props = {
  date: string;
  branchId: number;
  branchTimeZone: string;
};

export const UtilizationGraph = ({ date, branchId, branchTimeZone }: Props) => {
  const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
  const { themeStyles } = useTheme();
  const { handleTenantError }: any = useTenant();
  const { timeZoneName }: any = useGeneral();
  const [dayName, setDayName] = useState(days[0]);
  const [daysValue, setDaysValue] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (branchId > 0) {
      axios
        .get(`${baseURL}utilizationGraph/TotalVehicleCountByBranchId/${branchId}`, {
          headers: {
            'X-Tenant-ID': tenantId,
            "Content-Type": "application/json"
          },
        })
        .then((response) => {
          setTotalCount(response?.data);
          handleTenantError(null);
        })
        .catch((error) => {
          handleTenantError(error);
        });
    } else {
      axios
        .get(`${baseURL}utilizationGraph/TotalVehicleCount`, {
          headers: {
            'X-Tenant-ID': tenantId,
            "Content-Type": "application/json"
          },
        })
        .then((response) => {
          setTotalCount(response?.data);
          handleTenantError(null);
        })
        .catch((error) => {
          handleTenantError(error);
        });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  useEffect(() => {
    if (!date) return;

    setLoading(true);

    const controller = new AbortController();
    const signal = controller.signal;

    const fetchData = async () => {
      try {
        let url = "";
        if (branchId > 0) {
          const updatedDate = moment.tz(date, branchTimeZone).endOf('day');
          const formatDateString = updatedDate.format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');

          url = `${baseURL}utilizationGraph/GetUtilizationGraphByBranchId/${formatDateString},${branchId}`;
        } else {
          const updatedDate = moment.tz(date, timeZoneName).endOf('day');
          const formatDateString = updatedDate.format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');

          url = `${baseURL}utilizationGraph?startDate=${formatDateString}`;
        }

        const response = await axios.get(url, {
          headers: {
            'X-Tenant-ID': tenantId,
            'Content-Type': 'application/json',
          },
          signal // attach signal for cancellation
        });

        const responseData = response?.data || [];

        let newDaysValue: any[] = [];
        switch (dayName) {
          case days[0]:
            newDaysValue = responseData?.slice(0, 7);
            break;
          case days[1]:
            newDaysValue = responseData?.slice(0, 14);
            break;
          case days[2]:
            newDaysValue = responseData?.slice(0, 30);
            break;
        }

        setData(responseData);
        setDaysValue(newDaysValue);
        setLoading(false);
        handleTenantError(null);
      } catch (error: any) {
        if (!axios.isCancel(error)) {
          setLoading(false);
          handleTenantError(error);
        }
      }
    };

    fetchData();

    // Cleanup: cancel previous request if branch or date changes
    return () => controller.abort();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, branchId, branchTimeZone, timeZoneName]);

  const onDateChange = (daysName: string) => {
    setLoading(true);
    setDayName(daysName)

    switch (daysName) {
      case days[0]:
        setDaysValue(() => data?.filter((_, index: number) => index < 7));
        break;
      case days[1]:
        setDaysValue(() => data?.filter((_, index: number) => index < 14));
        break;
      case days[2]:
        setDaysValue(() => data?.filter((_, index: number) => index < 30));
        break;
    }

    setLoading(false);
  }

  return (
    <div className={`border ${themeStyles.main.borderColor} rounded-[16px] p-4 w-full max-w-md`}>
      <div className="flex justify-between items-center mb-4">

        <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>Utilization Graph</h2>

        <button
          className={`flex items-center justify-between py-[5px] px-[6px] gap-1.5 rounded-lg border border-solid ${themeStyles.main.borderColor}`}
          aria-controls={open ? 'basic-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
        >
          <span className={`${themeStyles.main.textHeadingColor} text-[10px] font-semibold`}>{dayName}</span>
          <span className={`material-symbols-outlined text-[18px] font-light ${themeStyles.main.textHeadingColor}`}>
            calendar_today
          </span>
        </button>

        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          slotProps={{
            paper: {
              sx: {
                width: 170,
                backgroundColor: themeStyles.topBar.bg
              }
            }
          }}
        >
          {days.map((day: string, index) => (
            <MenuItem
              key={index}
              sx={{ fontSize: "12px", marginBlock: "0px" }}
              onClick={() => {
                onDateChange(day);
                setAnchorEl(null);
              }}>

              <span className={`${themeStyles.topBar.tailwindTextColor} text-[16px] font-medium`}>{day}</span>

            </MenuItem>
          ))}
        </Menu>
      </div>

      <div className="h-[340px] overflow-y-auto">
        {
          loading && (
            <div className="w-full h-full flex items-center justify-center">
              <CircularProgress />
            </div>
          )
        }

        {
          !loading && (
            daysValue?.length > 0 ?
              daysValue?.map((item: any, index) => {
                const itemCount = parseInt(item?.count || "0");
                const total = parseInt(totalCount || "0");
                const percentage = total > 0 ? (itemCount * 100) / total : 0;

                return (
                  <div
                    key={index}
                    className="mb-2"
                  >
                    <span className={`w-full text-xs font-medium ${themeStyles.main.textSubHeadingColor}`}>
                      {(() => {
                        if (!date) return "Invalid date";

                        const baseDate = new Date(date);
                        if (isNaN(baseDate.getTime())) return "Invalid date";

                        const daysOffset = daysValue?.length - index - 1;
                        const adjustedDate = new Date(baseDate);
                        adjustedDate.setDate(baseDate.getDate() - daysOffset);

                        return format(adjustedDate, "MMMM d");
                      })()}
                    </span>

                    <div className="flex items-center justify-between gap-[30px]">
                      <span className={`text-xs font-semibold ${themeStyles.main.textHeadingColor}`}>({itemCount}/{total > 0 ? total : 0})</span>
                      <div className="flex-1 mx-2 h-[10px] bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${colorConfigs?.btn?.main?.tailwind} rounded-full`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className={`text-xs font-semibold ${themeStyles.main.textHeadingColor}`}>{percentage?.toFixed(1)}%</span>
                    </div>
                  </div>
                )
              }) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className={`text-xs font-semibold ${themeStyles.main.textSubHeadingColor}`}>No data</span>
                </div>
              )
          )
        }
      </div>
    </div>
  );
}